import React, { useMemo, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useApolloClient, useQuery } from "@apollo/client";
import _ from "lodash";
import { jwtDecode } from "jwt-decode";
import { useKeycloak } from "@react-keycloak/web";
import { Box, Menu, MenuItem, ListSubheader } from "@mui/material";
import { IconButton } from "@mui/joy";
import { MoreVertRounded, EditRounded, DeleteRounded, NotificationsActiveRounded, NotificationAddRounded } from "@mui-symbols-material/w300";
import { useTranslation } from "react-i18next";
import { SystemClientRegion } from "../../graphql/queries/systemClientRegion";
import { ProfileClientRegion } from "../../graphql/queries/profileClientRegion";
import { InstalledBaseNodeSubscriptions } from "../../graphql/queries/installedBaseNodeSubscriptions";
import { InstalledBaseNode } from "../../codegen/graphql";
import EditNodeModal from "../EditNodeModal";
import DeleteNodeModal from "../InstalledBaseTreeColumn/DeleteNodeModal";
import NodeFavoriteButton from "../NodeFavoriteButton";
import NodeSubscriptionModal from "../NodeSubscriptionModal";

interface Props {
    systemId: string
    /** Closes the system's open tab after it was deleted (see SystemRoutes). */
    onSystemDeleted?: (systemId: string) => void
    /** Editing the IoT connection only makes sense once one is registered. */
    hasIotConnection?: boolean
}

type MenuEntry = {
    key: string
    label: React.ReactNode
    onClick: () => void
}

type MenuCategory = {
    key: string
    label: string
    entries: MenuEntry[]
}

const SystemDashboardActions = ({ systemId, onSystemDeleted, hasIotConnection }: Props) => {
    const { t } = useTranslation();
    const { keycloak } = useKeycloak();
    const navigate = useNavigate();
    const client = useApolloClient();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [editNodeModalOpen, setEditNodeModalOpen] = useState(false);
    const [deleteNodeModalOpen, setDeleteNodeModalOpen] = useState(false);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const menuOpen = Boolean(anchorEl);

    const roles = useMemo(() => {
        if (!keycloak.token) return [] as string[];
        try {
            const decoded = jwtDecode<Record<string, unknown>>(keycloak.token);
            return _.get(decoded, 'otto_access.roles', []) as string[];
        } catch { return [] as string[]; }
    }, [keycloak.token]);

    const isGlobalAdmin = roles.includes('globalAdmin');
    const isRegionAdmin = roles.includes('regionAdmin');
    const isClientAdmin = roles.includes('clientAdmin');

    const systemQuery = useQuery(SystemClientRegion, {
        variables: { id: systemId },
        skip: !systemId,
    });
    const profileQuery = useQuery(ProfileClientRegion);

    const systemType = _.get(systemQuery, 'data.system.systemType') ?? null;
    const systemClientId: string | null = _.get(systemQuery, 'data.system.client.id', null);
    const systemRegionId: string | null = _.get(systemQuery, 'data.system.client.region.id', null);
    const installedBaseNode = _.get(systemQuery, 'data.system.installedBaseNode') as InstalledBaseNode | undefined;
    const installedBaseNodeId = installedBaseNode?.id;
    // Same gates as the tree's right-click actions for non-root nodes
    // (nodeActions.tsx) — a system is never a tree root, so the
    // installedBaseRootNode* resources don't apply here.
    const canWriteNode = !!installedBaseNode?.access?.write;
    const canDeleteNode = !!installedBaseNode?.access?.delete;

    const { data: subscriptionsData } = useQuery(InstalledBaseNodeSubscriptions, {
        fetchPolicy: 'cache-first',
        skip: !installedBaseNodeId,
    });

    const isAlreadySubscribed = useMemo(() => {
        if (!installedBaseNodeId) return false;
        const subs = (subscriptionsData?.installedBaseNodeSubscriptionList?.subscriptions ?? []) as any[];
        return subs.some((sub) => sub?.installedBaseNode?.id === installedBaseNodeId);
    }, [subscriptionsData, installedBaseNodeId]);
    const userClientId: string | null = _.get(profileQuery, 'data.profile.client.id', null);
    const userRegionId: string | null = _.get(profileQuery, 'data.profile.client.region.id', null);

    const canEdit = useMemo(() => {
        if (systemType === null || !hasIotConnection) return false; // only IoT systems with a registered connection
        if (isGlobalAdmin) return true;
        if (isRegionAdmin) return !!userRegionId && userRegionId === systemRegionId;
        if (isClientAdmin) return !!userClientId && userClientId === systemClientId;
        return false;
    }, [systemType, hasIotConnection, isGlobalAdmin, isRegionAdmin, isClientAdmin, userRegionId, systemRegionId, userClientId, systemClientId]);

    const categories: MenuCategory[] = useMemo(() => {
        const result: MenuCategory[] = [
            {
                key: 'actions',
                label: t('Actions'),
                entries: [
                    ...(installedBaseNode?.id && canWriteNode ? [{
                        key: 'edit',
                        label: (
                            <Box display="flex" alignItems="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', mr: '4px' }}>
                                    <EditRounded fontSize="inherit" color="inherit" />
                                </Box>
                                {t('Edit system')}
                            </Box>
                        ),
                        onClick: () => setEditNodeModalOpen(true),
                    }] : []),
                    // Same dialog as the tree's Delete — the preview decides
                    // trash vs. purge and whether the delete is permitted.
                    ...(installedBaseNode?.id && canDeleteNode ? [{
                        key: 'delete',
                        label: (
                            <Box display="flex" alignItems="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', mr: '4px' }}>
                                    <DeleteRounded fontSize="inherit" color="inherit" />
                                </Box>
                                {t('Delete system')}
                            </Box>
                        ),
                        onClick: () => setDeleteNodeModalOpen(true),
                    }] : []),
                ],
            },
            {
                key: 'iot',
                label: t('IoT Connection'),
                entries: canEdit
                    ? [{
                        key: 'edit',
                        label: (
                            <Box display="flex" alignItems="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', mr: '4px' }}>
                                    <EditRounded fontSize="inherit" color="inherit" />
                                </Box>
                                {t('Edit')}
                            </Box>
                        ),
                        onClick: () => navigate(`/systems/${systemId}/iotEdit`),
                    }]
                    : [],
            },
        ];
        return result.filter((c) => c.entries.length > 0);
    }, [canEdit, t, navigate, systemId, installedBaseNode, canWriteNode, canDeleteNode]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    return (
        <Box display="flex" alignItems="center">
            <NodeFavoriteButton
                nodeId={installedBaseNode?.id}
                isStarred={!!installedBaseNode?.isStarred}
                variant="outlined"
                sx={{mr: '5px', flexShrink: 0}}
            />
            {installedBaseNodeId && (
                <IconButton
                    variant="outlined"
                    color="neutral"
                    sx={{mr: '5px', flexShrink: 0}}
                    aria-label={isAlreadySubscribed
                        ? t('Edit notification subscription')
                        : t('Subscribe to notifications')
                    }
                    title={isAlreadySubscribed
                        ? t('Edit notification subscription')
                        : t('Subscribe to notifications')
                    }
                    onClick={() => setSubscriptionModalOpen(true)}
                >
                    {isAlreadySubscribed
                        ? <NotificationsActiveRounded/>
                        : <NotificationAddRounded/>
                    }
                </IconButton>
            )}
            {categories.length > 0 &&
                <IconButton
                    variant="outlined"
                    aria-controls={menuOpen ? 'system-dashboard-actions-menu' : undefined}
                    aria-expanded={menuOpen ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={handleMenuOpen}
                >
                    <MoreVertRounded />
                </IconButton>
            }
            <Menu
                id="system-dashboard-actions-menu"
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {categories.flatMap((category) => [
                    <ListSubheader
                        key={`${category.key}-header`}
                        disableSticky
                        sx={{
                            bgcolor: 'grey.100',
                            color: 'text.secondary',
                            lineHeight: '28px',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                        }}
                    >
                        {category.label}
                    </ListSubheader>,
                    ...category.entries.map((entry) => (
                        <MenuItem
                            key={`${category.key}-${entry.key}`}
                            disableRipple
                            onClick={() => {
                                entry.onClick();
                                handleMenuClose();
                            }}
                        >
                            {entry.label}
                        </MenuItem>
                    )),
                ])}
            </Menu>
            {editNodeModalOpen && installedBaseNode &&
                <EditNodeModal
                    open={editNodeModalOpen}
                    onClose={() => setEditNodeModalOpen(false)}
                    node={installedBaseNode}
                    refetchFunction={() => {
                        client.refetchQueries({
                            // SystemVisualizationUrl backs the side navigation's Visualization
                            // entry — without it the entry only appears after a page reload.
                            include: ["SystemBreadcrumbPath", "SystemNameplateInfo", "SystemTabInfo", "SystemClientRegion", "SystemVisualizationUrl"],
                        });
                    }}
                />
            }
            {deleteNodeModalOpen && installedBaseNodeId && installedBaseNode &&
                <DeleteNodeModal
                    open={deleteNodeModalOpen}
                    onClose={() => setDeleteNodeModalOpen(false)}
                    nodeId={installedBaseNodeId}
                    node={installedBaseNode}
                    // The system is gone from the list once it is trashed.
                    refetchFunction={() => {
                        client.refetchQueries({
                            include: ["SearchSystems", "SystemList"],
                        });
                    }}
                    // Tab ids are system ids, not node ids.
                    deleteNodeCallback={() => onSystemDeleted?.(systemId)}
                />
            }
            {subscriptionModalOpen && installedBaseNodeId &&
                <NodeSubscriptionModal
                    open={subscriptionModalOpen}
                    onClose={() => setSubscriptionModalOpen(false)}
                    installedBaseNodeId={installedBaseNodeId}
                />
            }
        </Box>
    );
};

export default SystemDashboardActions;
